import { useCallback, useEffect, useState } from 'react';
import { apiJson } from '../api/client';
import {
  fetchGallery,
  getGalleryEntry,
  loadMoreGallery,
  mutateGallery,
  subscribeGallery,
  STALE_MS,
} from './galleryCache';

export interface UseGalleryResult<T> {
  items: T[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
  /** Stage 12 (C4): append the next page. No-op at the end. */
  loadMore: () => Promise<void>;
  /** True when more pages are available. */
  hasMore: boolean;
}

/**
 * Generic gallery hook. Public API extended in Stage 12 (C4) with
 * `loadMore` and `hasMore` for cursor-based infinite scroll. Existing
 * callers that never touch those stay backward compatible.
 */
export function useGallery<T extends { id: string }>(
  endpoint: string,
): UseGalleryResult<T> {
  const [, forceRerender] = useState(0);

  useEffect(() => {
    const unsub = subscribeGallery(endpoint, () =>
      forceRerender((n) => n + 1),
    );
    const entry = getGalleryEntry<T>(endpoint);
    const shouldFetch =
      !entry ||
      entry.lastFetchedAt === 0 ||
      Date.now() - entry.lastFetchedAt > STALE_MS;
    if (shouldFetch) {
      void fetchGallery<T>(endpoint).catch(() => {
        /* error already stored on the entry */
      });
    }
    return unsub;
  }, [endpoint]);

  const entry = getGalleryEntry<T>(endpoint);
  const items = entry?.items ?? [];
  const loading = entry?.loading ?? !entry;
  const error = entry?.error ?? null;
  const hasMore = entry?.nextCursor !== null && entry?.nextCursor !== undefined;

  const refresh = useCallback(async (): Promise<void> => {
    await fetchGallery<T>(endpoint, true);
  }, [endpoint]);

  const deleteItem = useCallback(
    async (id: string): Promise<void> => {
      await apiJson('DELETE', `${endpoint}/${encodeURIComponent(id)}`);
      mutateGallery<T>(endpoint, (arr) => arr.filter((it) => it.id !== id));
    },
    [endpoint],
  );

  const loadMore = useCallback(async (): Promise<void> => {
    await loadMoreGallery<T>(endpoint);
  }, [endpoint]);

  return { items, loading, error, refresh, deleteItem, loadMore, hasMore };
}
